import React, { useState, useEffect } from "react";
import {
  X,
  FileText,
  Building,
  User,
  Truck,
  Calendar,
  Clock,
  Package,
  AlertCircle,
  CheckCircle,
  XCircle,
  Download,
  Printer,
  Upload,
  RotateCcw,
} from "lucide-react";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import API_BASE_URL from "../config";
import megalogo from '../Image/megalogo.png';

const STATUS_MAP = {
  0: { label: "Draft", color: "gray", icon: AlertCircle },
  1: { label: "Pending", color: "yellow", icon: Clock },
  2: { label: "Approved", color: "green", icon: CheckCircle },
  3: { label: "Rejected", color: "red", icon: XCircle },
};

const PurchaseOrderModal = ({ order, onClose, companies, vendors }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [customLogo, setCustomLogo] = useState(null);
  const [showLogoUploader, setShowLogoUploader] = useState(false);
  const [approval, setApproval] = useState(null);
  const [approver, setApprover] = useState(null);
  const [departmentName, setDepartmentName] = useState("—");
  const [locationName, setLocationName] = useState("—");
  const [vendorDetails, setVendorDetails] = useState(null);

  useEffect(() => {
    if (order) {
      fetchAllData();
    }
  }, [order]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      const detailsRes = await fetch(`${API_BASE_URL}api/T_Po_Details/POHeader/${order.id}`);
      if (detailsRes.ok) {
        const detailsData = await detailsRes.json();
        setItems(detailsData);
      }

      const websiteRes = await fetch(`${API_BASE_URL}api/Website_Details`);
      if (websiteRes.ok) {
        const websiteData = await websiteRes.json();
        const activeWebsite = websiteData.find(w => w.isActive) || websiteData[0];
        if (activeWebsite) setWebsiteInfo(activeWebsite);
      }

      if (order.vendorId) {
        const vendorRes = await fetch(`${API_BASE_URL}api/Vendor/${order.vendorId}`);
        if (vendorRes.ok) {
          const vendorData = await vendorRes.json();
          setVendorDetails(vendorData); // ✅ PascalCase fields: VendorName, Address1, ContactPerson, etc.
        }
      }

      if (order.departmentId) {
        const deptRes = await fetch(`${API_BASE_URL}api/Department/${order.departmentId}`);
        if (deptRes.ok) {
          const deptData = await deptRes.json();
          setDepartmentName(deptData.departmentName || "—");
        }
      }

      if (order.locationId) {
        const locRes = await fetch(`${API_BASE_URL}api/Location/${order.locationId}`);
        if (locRes.ok) {
          const locData = await locRes.json();
          setLocationName(locData.locationName || "—");
        }
      }

      const approvalsRes = await fetch(`${API_BASE_URL}api/T_PO_Approval`);
      if (approvalsRes.ok) {
        const approvalsData = await approvalsRes.json();
        const poApproval = approvalsData.find(
          a => a.poHeaderId === order.id && a.response === 1
        );

        if (poApproval) {
          setApproval(poApproval);
          const usersRes = await fetch(`${API_BASE_URL}api/Users`);
          if (usersRes.ok) {
            const usersData = await usersRes.json();
            const approverUser = usersData.find(u => u.id === poApproval.approverId);
            if (approverUser) setApprover(approverUser);
          }
        }
      }

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Helper formatter - lagay sa taas ng component, after formatDate
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatQty = (qty) => {
    return new Intl.NumberFormat('en-PH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(qty || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const statusInfo = STATUS_MAP[status] || STATUS_MAP[0];
    const Icon = statusInfo.icon;
    const colorClasses = {
      gray: "bg-gray-100 text-gray-800",
      yellow: "bg-yellow-100 text-yellow-800",
      green: "bg-green-100 text-green-800",
      red: "bg-red-100 text-red-800",
    };
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${colorClasses[statusInfo.color]}`}>
        <Icon size={14} />
        {statusInfo.label}
      </span>
    );
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => {
      return sum + (item.qty * item.unitPrice);
    }, 0);
  };
  const calculateTotalDiscount = () => {
    return items.reduce((sum, item) => {
      const lineTotal = item.qty * item.unitPrice;
      return sum + (lineTotal * item.detailDiscount) / 100;
    }, 0);
  };

  const calculateVAT = () => {
    if (items.length === 0) return 0;
    if (order.isNonVAT) return 0;
    if (!order.isVatInclusive) return 0;
    return (calculateSubtotal() - calculateTotalDiscount()) * 0.12;
  };
  const calculateTotal = () => {
    return calculateSubtotal() - calculateTotalDiscount() + calculateVAT();
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { alert('File size must be less than 2MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => { setCustomLogo(reader.result); setShowLogoUploader(false); };
    reader.readAsDataURL(file);
  };

  const handleResetLogo = () => { setCustomLogo(null); setShowLogoUploader(false); };
  const getLogoSrc = () => customLogo || megalogo;
  const logoSrc = getLogoSrc();

  const handleExportToPDF = async () => {
    const element = document.getElementById("po-pdf-content");
    const canvas = await html2canvas(element, {
      scale: 2, useCORS: true, backgroundColor: "#ffffff",
      windowHeight: element.scrollHeight, height: element.scrollHeight
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }
    pdf.save(`PO-${order.poNumber}.pdf`);
  };

  if (!order) {
    order = {
      id: 1, poNumber: "PO-2024-001", companyId: 1, vendorId: 1, status: 2,
      receivingStatus: "Completed", poCreatedDate: new Date(), deliveryDate: new Date(),
      dateSubmittedForApproval: new Date(), paymentTerms: "Net 30", referencePRId: "PR-2024-001",
      departmentId: 1, locationId: 2, preparedBy: "John Doe", shipTo_Location: "Main Warehouse",
      shipTo_Address1: "123 Main St", shipTo_Address2: "Building A",
      shipTo_ContactPerson: "Jane Smith", shipTo_ContactNo: "+63 912 345 6789",
      remarks: "Please deliver by end of month", isVatInclusive: true, isNonVAT: false, headerDiscount: 5
    };
  }

  if (!companies) companies = { 1: "ABC Corporation" };
  if (!vendors) vendors = { 1: "XYZ Supplies Inc." };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full my-8">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <FileText className="text-white" size={32} />
                <h2 className="text-2xl font-bold text-white">Purchase Order Details</h2>
              </div>
              <p className="text-blue-100 text-sm">Complete information about this purchase order</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X size={24} className="text-white" />
            </button>
          </div>
        </div>

        {/* CONTENT */}
        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="no-print flex justify-end gap-3 mb-4">
         
            <button onClick={handleExportToPDF}
              className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all">
              <Download size={20} /> PDF
            </button>
          </div>

          <div id="po-pdf-content" className="bg-white p-8 font-sans">
            {/* HEADER SECTION */}
            <div className="mb-8">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <img src={logoSrc} alt="Company Logo" className="h-28 w-auto object-contain drop-shadow-md"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <div className="text-sm text-gray-700 space-y-0.5">
                  {websiteInfo?.websiteAddress && <p>{websiteInfo.websiteAddress}</p>}
                  {websiteInfo?.websiteContact && <p>Tel No.: {websiteInfo.websiteContact}</p>}
                  {websiteInfo?.websiteTIN && <p className="font-medium">Vat Reg.: {websiteInfo.websiteTIN}</p>}
                </div>
              </div>

              <div className="border-t-2 border-gray-300 mb-4"></div>

              <div className="flex justify-end mb-4">
                <div className="text-right bg-gray-50 px-6 py-3 rounded-lg border border-gray-200">
                  <p className="text-sm mb-1">
                    <span className="font-semibold text-gray-700">PO Date:</span>{" "}
                    <span className="text-gray-900">{formatDate(order.poCreatedDate)}</span>
                  </p>
                  <p className="text-sm">
                    <span className="font-semibold text-gray-700">PO No:</span>{" "}
                    <span className="text-blue-700 font-bold">{order.poNumber}</span>
                  </p>
                </div>
              </div>

              <div className="text-center py-4 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-md">
                <h2 className="text-3xl font-bold text-white tracking-widest">PURCHASE ORDER</h2>
              </div>
            </div>

            {/* TWO COLUMN LAYOUT */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* LEFT: VENDOR INFORMATION */}
              <div className="border-2 border-gray-300 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-blue-50 px-4 py-2 border-b-2 border-blue-200">
                  <h3 className="font-bold text-sm uppercase text-blue-800">Vendor Information</h3>
                </div>
                <div className="p-4 space-y-2 text-sm bg-white">
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-24">Name:</span>
                    <span className="text-gray-900 font-medium">
                      {/* ✅ PascalCase - galing sa API response */}
                      {vendorDetails?.VendorName || vendors[order.vendorId] || "—"}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-24">Address:</span>
                    <span className="text-gray-900">{vendorDetails?.Address1 || "—"}</span>
                  </div>
                  {vendorDetails?.Address2 && (
                    <div className="pl-24 text-gray-700">{vendorDetails.Address2}</div>
                  )}
                  {vendorDetails?.Address3 && (
                    <div className="pl-24 text-gray-700">{vendorDetails.Address3}</div>
                  )}
                  <div className="flex pt-2 border-t border-gray-200">
                    <span className="font-semibold text-gray-700 w-24">Contact:</span>
                    <span className="text-gray-900">{vendorDetails?.ContactPerson || "—"}</span>
                  </div>
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-24">Tel No:</span>
                    <span className="text-gray-900">{vendorDetails?.ContactNo || "—"}</span>
                  </div>
                  <div className="flex pt-2 border-t border-gray-200">
                    <span className="font-semibold text-gray-700 w-24">TIN No:</span>
                    <span className="text-gray-900 font-mono">{vendorDetails?.TIN || "—"}</span>
                  </div>
                </div>
              </div>

              {/* RIGHT: DELIVERY INSTRUCTION */}
              <div className="border-2 border-gray-300 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-green-50 px-4 py-2 border-b-2 border-green-200">
                  <h3 className="font-bold text-sm uppercase text-green-800">Delivery Instruction</h3>
                </div>
                <div className="p-4 space-y-2 text-sm bg-white">
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-20">Ship To:</span>
                    <span className="text-gray-900 font-medium">{order.shipTo_Location || "—"}</span>
                  </div>
                  {order.shipTo_Address1 && (
                    <div className="pl-20 text-gray-700">{order.shipTo_Address1}</div>
                  )}
                  {order.shipTo_Address2 && (
                    <div className="pl-20 text-gray-700">{order.shipTo_Address2}</div>
                  )}
                  <div className="pt-3">
                    <div className="border-2 border-red-500 bg-red-50 px-3 py-2 rounded inline-block">
                      <span className="font-semibold text-red-700">Del Date:</span>{" "}
                      <span className="text-red-900 font-bold">{formatDate(order.deliveryDate)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* BILL TO SECTION */}

            {/* ITEMS TABLE */}
            <div className="mb-6 shadow-lg rounded-lg overflow-hidden mt-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <th className="border border-blue-500 px-3 py-3 text-left font-semibold">Item Description</th>
                    <th className="border border-blue-500 px-2 py-3 text-center w-24">Specification</th>
                    <th className="border border-blue-500 px-2 py-3 text-center w-16">Qty</th>
                    <th className="border border-blue-500 px-2 py-3 text-center w-20">Unit</th>
                    <th className="border border-blue-500 px-2 py-3 text-right w-28">Price</th>
                    <th className="border border-blue-500 px-2 py-3 text-right w-28">Amt VatEx</th>
                    <th className="border border-blue-500 px-2 py-3 text-right w-24">Vat Amt</th>
                    <th className="border border-blue-500 px-2 py-3 text-right w-20">Disc</th>
                    <th className="border border-blue-500 px-2 py-3 text-right w-28">Total Amt</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {items.length > 0 ? (
                    items.map((item, idx) => {
                      const lineTotal = item.qty * item.unitPrice;
                      const discount = (lineTotal * item.detailDiscount) / 100;
                      const amtVatEx = lineTotal - discount;
                      const vatAmt = (order.isNonVAT || !order.isVatInclusive) ? 0 : amtVatEx * 0.12;
                      const totalAmt = amtVatEx + vatAmt;

                      return (
                        <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border border-gray-300 px-3 py-2.5 text-gray-800">{item.itemDescription}</td>
                          <td className="border border-gray-300 px-2 py-2.5 text-center text-gray-600">{item.specification}</td>
                          {/* ✅ Qty - walang peso sign, may comma, no trailing .00 */}
                          <td className="border border-gray-300 px-2 py-2.5 text-center font-semibold text-gray-900">
                            {formatQty(item.qty)}
                          </td>
                          <td className="border border-gray-300 px-2 py-2.5 text-center text-gray-700">{item.uom}</td>
                          {/* ✅ May peso sign + comma */}
                          <td className="border border-gray-300 px-2 py-2.5 text-right text-gray-900">{formatCurrency(item.unitPrice)}</td>
                          <td className="border border-gray-300 px-2 py-2.5 text-right text-gray-900">{formatCurrency(amtVatEx)}</td>
                          <td className="border border-gray-300 px-2 py-2.5 text-right text-blue-700">{formatCurrency(vatAmt)}</td>
                          <td className="border border-gray-300 px-2 py-2.5 text-right text-red-600">{formatCurrency(discount)}</td>
                          <td className="border border-gray-300 px-2 py-2.5 text-right font-bold text-green-700">{formatCurrency(totalAmt)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="border border-gray-300 px-2 py-8 text-center text-gray-500">
                        <div className="text-4xl mb-2">📦</div>
                        <div>No items found</div>
                      </td>
                    </tr>
                  )}

                  {items.length > 0 && items.length < 5 && (
                    <>
                      {[...Array(5 - items.length)].map((_, i) => (
                        <tr key={`empty-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          {[...Array(9)].map((_, j) => (
                            <td key={j} className="border border-gray-300 px-2 py-2.5">&nbsp;</td>
                          ))}
                        </tr>
                      ))}
                      <tr className="bg-yellow-50">
                        <td colSpan="9" className="border-2 border-yellow-400 px-2 py-4 text-center font-bold text-gray-800 text-base">
                          ★★★★ NOTHING FOLLOWS ★★★★
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* BOTTOM SECTION */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="border-2 border-gray-300 rounded-lg p-4 bg-amber-50 shadow-sm">
                <div className="text-sm">
                  <span className="font-bold text-gray-800">Remarks:</span>{" "}
                  <span className="text-gray-700">{order.remarks || "—"}</span>
                </div>
              </div>

              <div className="border-2 border-gray-300 rounded-lg overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="bg-gray-50">
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-700 border-b border-gray-200">Total Amount Vat Ex</td>
                      <td className="px-4 py-2.5 text-right font-bold text-gray-900 border-b border-gray-200">
                        {formatCurrency(calculateSubtotal())}
                      </td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-700 border-b border-gray-200">Vat</td>
                      <td className="px-4 py-2.5 text-right font-bold text-blue-700 border-b border-gray-200">
                        {formatCurrency(calculateVAT())}
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-700 border-b-2 border-gray-400">Less Discount</td>
                      <td className="px-4 py-2.5 text-right font-bold text-red-600 border-b-2 border-gray-400">
                        -{formatCurrency(calculateTotalDiscount())}
                      </td>
                    </tr>
                    <tr className="bg-green-50">
                      <td className="px-4 py-3 text-right font-bold text-gray-900 text-base">Total Amount</td>
                      <td className="px-4 py-3 text-right font-bold text-green-700 text-lg">
                        {formatCurrency(calculateTotal())}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* SIGNATURE SECTION */}
            {/* SIGNATURE SECTION */}
            <div className="mt-16 pt-8 border-t-2 border-gray-400">
              <div className="flex justify-center">
                <div className="text-center w-72">
                  <div className="h-24 flex items-end justify-center mb-3 border-b-2 border-gray-800 pb-2">
                    {approver ? (
                      <img
                        src={`${API_BASE_URL}api/Users/${approver.id}/digitalsign`}
                        alt="Signature"
                        className="h-16 object-contain"
                        crossOrigin="anonymous"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <span className="text-gray-400 italic  p-8 text-xs">Signature over printed name</span>

                    )}
                    {approver ? `${approver.firstName} ${approver.lastName}` : ""}

                  </div>
                  <div className="text-sm  font-bold text-gray-900 mt-1">
                  </div>
                  <div className="text-sm font-bold text-gray-800 uppercase tracking-wider mt-1">Approved By</div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose}
            className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-semibold">
            Close
          </button>
        </div>
      </div>

      {showLogoUploader && (
        <div className="no-print fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Upload Custom Logo</h3>
              <button onClick={() => setShowLogoUploader(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer"
                onClick={() => document.getElementById('logoInput').click()}>
                <Upload size={48} className="mx-auto text-gray-400 mb-3" />
                <p className="text-gray-700 font-medium mb-1">Click to upload logo</p>
                <p className="text-sm text-gray-500">PNG, JPG up to 2MB</p>
              </div>
              <input type="file" id="logoInput" accept="image/*" onChange={handleLogoUpload} className="hidden" />
              {customLogo && (
                <button onClick={handleResetLogo}
                  className="w-full py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors">
                  Reset to Default
                </button>
              )}
            </div>
          </div>
        </div>
      )}

<style>{`
    @media print {
        .no-print { display: none !important; }
        body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
        @page { margin: 1cm; size: A4; }
    }
`}</style>
    </div>
  );
};

export default PurchaseOrderModal;